/**
 * ShareDialog View Handler
 *
 * Handles deep linking for share dialog views, supporting auto-opening the
 * ShareBetSlipDialog with specific contracts or products through URL parameters.
 *
 * URL formats:
 * - Single contract: ?view=sharedialog&conid=12345
 * - Product (all candidates): ?view=sharedialog&productConid=999
 * - With eh_* prefix: ?eh_view=sharedialog&eh_product_conid=848766118
 */

import { registerViewHandler } from '@/utils/deepLink';
import type { BetSlipParams } from '@/types/viewParams.types';
import { onBeforeUnmount } from 'vue';

/**
 * Setup share dialog deep linking integration
 * This should be called from App.vue or a layout component
 * @returns Cleanup function to unregister the handler
 */
export function setupShareDialogDeepLink() {
  const cleanup = registerViewHandler('sharedialog', async (params: BetSlipParams) => {
    const conid = params.conid ? Number(params.conid) : undefined;
    const productConid = params.productConid ? Number(params.productConid) : undefined;

    if (conid && !isNaN(conid) && conid > 0) {
      const event = new CustomEvent('share-dialog-open-request', {
        detail: {
          conid,
          productConid: undefined,
          mode: 'conid',
        },
      });
      window.dispatchEvent(event);
    } else if (productConid && !isNaN(productConid) && productConid > 0) {
      const event = new CustomEvent('share-dialog-open-request', {
        detail: {
          conid: undefined,
          productConid,
          mode: 'product',
        },
      });
      window.dispatchEvent(event);
    }
  });

  return cleanup;
}

/**
 * Composable hook for share dialog deep linking
 * Use this in Vue components to automatically setup and cleanup deep linking
 * @returns Object with setup function and manual cleanup
 */
export function useShareDialogDeepLink() {
  let cleanup: (() => void) | null = null;

  const setup = () => {
    if (!cleanup) {
      cleanup = setupShareDialogDeepLink();
    }
  };

  const teardown = () => {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
  };

  // Auto cleanup on component unmount
  onBeforeUnmount(teardown);

  return {
    setup,
    teardown,
  };
}

/**
 * ShareDialog event types for deep linking integration
 */
export interface ShareDialogOpenEvent extends CustomEvent {
  detail: {
    conid?: number;
    productConid?: number;
    mode: 'conid' | 'product';
  };
}

// Type guard for event handling
export function isShareDialogOpenEvent(event: Event): event is ShareDialogOpenEvent {
  return event.type === 'share-dialog-open-request' && 'detail' in event;
}

/**
 * Event listener helper for share dialog open events
 * @param callback - Function to call when share dialog open is requested
 * @returns Cleanup function to remove the event listener
 */
export function onShareDialogOpenRequest(callback: (detail: ShareDialogOpenEvent['detail']) => void): () => void {
  const handler = (event: Event) => {
    if (isShareDialogOpenEvent(event)) {
      callback(event.detail);
    }
  };

  window.addEventListener('share-dialog-open-request', handler);

  return () => {
    window.removeEventListener('share-dialog-open-request', handler);
  };
}
